<template>
    <div class="EvolutionModal">
        <WLoading
            v-if="loading"
            visible
        />

        <div
            v-else-if="error"
            class="State"
        >
            <p class="Error">{{ error }}</p>
        </div>

        <div
            v-else-if="!captures.length"
            class="State"
        >
            <p>{{ $t('imports.evolutionModal.empty') }}</p>
            <WButton
                class="mt-1"
                :loading="capturing"
                @click="captureNow"
            >
                {{ $t('imports.evolutionModal.capture') }}
            </WButton>
        </div>

        <div
            v-else
            class="Content"
        >
            <div class="Selectors">
                <div class="Selector">
                    <label>{{ $t('imports.evolutionModal.from') }}</label>
                    <WSelect
                        name="evolutionFrom"
                        :options="dateOptions"
                        :value="fromKey"
                        @input="(value) => (fromKey = value)"
                    />
                </div>
                <div class="Selector">
                    <label>{{ $t('imports.evolutionModal.to') }}</label>
                    <WSelect
                        name="evolutionTo"
                        :options="dateOptions"
                        :value="toKey"
                        @input="(value) => (toKey = value)"
                    />
                </div>
            </div>

            <div class="Block">
                <div class="BlockTitle">
                    {{ $t('imports.evolutionModal.countTitle', { date: formatDate(fromCapture.capturedAt) }) }}
                </div>
                <table class="EvoTable">
                    <thead>
                        <tr>
                            <th
                                v-for="entity in entities"
                                :key="entity.slug"
                            >
                                {{ entity.label }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td
                                v-for="entity in entities"
                                :key="entity.slug"
                            >
                                {{ formatCount(fromCapture.counts[entity.slug]) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="Block">
                <div class="BlockTitle">
                    {{ $t('imports.evolutionModal.countTitle', { date: formatDate(toCapture.capturedAt) }) }}
                </div>
                <table class="EvoTable">
                    <thead>
                        <tr>
                            <th
                                v-for="entity in entities"
                                :key="entity.slug"
                            >
                                {{ entity.label }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="Highlight">
                            <td
                                v-for="entity in entities"
                                :key="entity.slug"
                            >
                                {{ formatCount(toCapture.counts[entity.slug]) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="Block">
                <div class="BlockTitle">
                    {{ $t('imports.evolutionModal.variation') }}
                </div>
                <table class="EvoTable">
                    <thead>
                        <tr>
                            <th
                                v-for="entity in entities"
                                :key="entity.slug"
                            >
                                {{ entity.label }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td
                                v-for="entity in entities"
                                :key="entity.slug"
                            >
                                {{ formatVariation(entity.slug) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p
                v-if="captures.length < 2"
                class="Hint"
            >
                {{ $t('imports.evolutionModal.needTwo') }}
            </p>

            <div class="Actions">
                <WButton
                    :loading="capturing"
                    @click="captureNow"
                >
                    {{ $t('imports.evolutionModal.capture') }}
                </WButton>
            </div>
        </div>
    </div>
</template>

<script>
import {
    Api, apiRequest, rethrowError, notifyError, notifySuccess,
} from '@/api'

export default {
    name: 'EvolutionModal',
    data () {
        return {
            loading: false,
            capturing: false,
            error: null,
            entities: [],
            captures: [],
            fromKey: null,
            toKey: null,
        }
    },
    computed: {
        dateOptions () {
            return this.captures.map((capture, index) => ({
                key: String(index),
                label: this.formatDate(capture.capturedAt),
            }))
        },
        fromCapture () {
            return this.captures[Number(this.fromKey)] || { counts: {} }
        },
        toCapture () {
            return this.captures[Number(this.toKey)] || { counts: {} }
        },
    },
    created () {
        this.fetchEvolution()
    },
    methods: {
        fetchEvolution () {
            this.loading = true
            this.error = null
            apiRequest(Api().snapshots.evolution())
                .then(({ entities, captures }) => {
                    this.entities = entities || []
                    this.captures = captures || []
                    if (this.captures.length) {
                        this.fromKey = '0'
                        this.toKey = String(this.captures.length - 1)
                    }
                })
                .catch(rethrowError(notifyError()))
                .finally(() => { this.loading = false })
        },
        captureNow () {
            this.capturing = true
            apiRequest(Api().snapshots.capture())
                .then(() => {
                    notifySuccess(this.$t('imports.evolutionModal.captureSuccess'))()
                    this.fetchEvolution()
                })
                .catch(rethrowError(notifyError()))
                .finally(() => { this.capturing = false })
        },
        formatDate (value) {
            if (!value) return ''
            const date = new Date(value)
            const pad = number => (`0${number}`).slice(-2)
            return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`
        },
        formatCount (value) {
            return value == null ? '—' : value
        },
        formatVariation (slug) {
            const from = this.fromCapture.counts[slug]
            const to = this.toCapture.counts[slug]
            if (from == null || to == null) return '—'
            if (from === 0) return to === 0 ? '0,0%' : '—'
            const pct = ((to - from) / from) * 100
            return `${pct.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
        },
    },
}
</script>

<style lang="scss" scoped>
.EvolutionModal {
    min-width: 280px;

    .State {
        padding: 10px 0;
        text-align: center;
    }

    .Error {
        color: $danger;
    }

    .Selectors {
        display: flex;
        margin-bottom: 20px;

        .Selector {
            flex: 1;

            & + .Selector {
                margin-left: 15px;
            }

            label {
                display: block;
                margin-bottom: 5px;
                color: #7a7a7a;
                font-size: 12px;
            }
        }
    }

    .Block {
        margin-bottom: 18px;
    }

    .BlockTitle {
        padding: 8px 12px;
        background: #2b2b2b;
        border-radius: 3px 3px 0 0;
        color: $white;
        font-weight: bold;
        font-size: 14px;
    }

    .EvoTable {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;

        th,
        td {
            padding: 10px 8px;
            text-align: center;
            white-space: nowrap;
        }

        thead th {
            font-weight: bold;
            border-bottom: 1px solid $gray-05;
        }

        tbody tr.Highlight td {
            background: #fcf3a8;
        }
    }

    .Hint {
        margin: -6px 0 10px;
        color: #7a7a7a;
        font-size: 12px;
    }

    .Actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 10px;
    }
}
</style>
